import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useFullRoutes } from '~/hooks';
import {
  useAddListingDetailsMutation,
  useGetListingCustomFieldsQuery,
  useGetListingDetailsQuery,
} from '~/redux/api/userApiSlice';
import { saveListingData } from '~addListing/utils/LocalStorage';
import { addListingDetailsType, ListingDetailsType } from '~addListing/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@aqarmap/aqarmap-ui';
import { useTranslation } from 'react-i18next';
import Loader from '~/components/Loader';
import { useAppDispatch } from '~/redux/hooks';
import { closeModal, openModal } from '~/redux/features/uiState/uiStateSlice';
import PriceInput from '~addListing/pages/PriceStep/PriceInput';
import PaymentMethod from '~addListing/pages/PriceStep/PaymentMethod';
import TotalInstallmentPrice from '~addListing/pages/PriceStep/TotalInstallmentPrice';
import DownPaymentInput from '~addListing/pages/PriceStep/DownPaymentInput';
import InstallmentsUnit from '~addListing/pages/PriceStep/InstallmentsUnit';
import {
  PAYMENT_METHOD_CASH_AND_INSTALLMENT_KEY,
  PAYMENT_METHOD_CASH_KEY,
  PAYMENT_METHOD_INSTALLMENT_KEY,
} from '~addListing/utils/data';
import RentalDuration from '~addListing/pages/PriceStep/RentalDuration';
import WeeklyInputPrice from '~addListing/pages/PriceStep/WeeklyInputPrice';
import DailyPriceInput from '~addListing/pages/PriceStep/DailyPriceInput';
import {
  listingPriceSchemaForRent,
  listingPriceSchemaForSale,
} from '~addListing/utils/schema';
import OverPrice from '~addListing/pages/PriceStep/OverPrice';
import SecurityDepositInput from '~addListing/pages/PriceStep/SecurityDepositInput';
import ContractPrice from '~addListing/pages/PriceStep/ContractPrice';
import InstallmentSummaryModal from '~addListing/pages/PriceStep/InstallmentSummaryModal';
import { InstallmentSummaryStateType } from '~addListing/types';

const MAX_DAYS = 30 * 360;

const PriceStep = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ADD_LISTING_URL } = useFullRoutes();
  const { t } = useTranslation('app');
  const dispatch = useAppDispatch();

  const { data, isFetching } = useGetListingDetailsQuery(id, {
    refetchOnMountOrArgChange: true,
  });
  const { data: customFieldsData, isLoading: isLoadingCustomField } =
    useGetListingCustomFieldsQuery(null);

  const [addListingDetails, { isLoading: isLoadingAddListingDetails }] =
    useAddListingDetailsMutation();
  const [InstallmentSummaryState, setInstallmentSummaryState] =
    useState<InstallmentSummaryStateType | null>(null);
  const [hasDownPayment, setHasDownPayment] = useState(true);

  const listingDetails: ListingDetailsType = data?.listing;
  const isForRent = listingDetails?.section.slug === 'for-rent';

  useEffect(() => {
    if (listingDetails?.payment_plans?.length) {
      setHasDownPayment(
        Boolean(listingDetails?.payment_plans?.[0]?.down_payment),
      );
    }
  }, [listingDetails]);

  const defaultContractPrice = listingDetails?.payment_plans?.[0]?.price || 0;
  const defaultOverPrice = listingDetails?.payment_plans?.[0]?.over_price || 0;
  const defaultSecurityDeposit =
    listingDetails?.payment_plans?.[0]?.security_deposit || 0;
  const methods = useForm<addListingDetailsType>({
    resolver: zodResolver(
      isForRent ? listingPriceSchemaForRent : listingPriceSchemaForSale,
    ),
  });

  const downPaymentType = methods.watch('downPaymentType') || 'amount';

  const overPrice = methods.watch('paymentPlans')?.[0]?.overPrice || 0;
  const securityDeposit =
    methods.watch('paymentPlans')?.[0]?.securityDeposit || 0;
  const contractPrice = methods.watch('paymentPlans')?.[0]?.price || 0;
  const totalPropertyPrice = contractPrice + overPrice;

  const paymentMethod = methods.watch('paymentMethod');
  const paymentMethodCash = paymentMethod === PAYMENT_METHOD_CASH_KEY;
  const paymentMethodCashAndInstallment =
    paymentMethod === PAYMENT_METHOD_CASH_AND_INSTALLMENT_KEY;
  const paymentMethodInstallment =
    paymentMethod === PAYMENT_METHOD_INSTALLMENT_KEY;

  const isValidPaymentPlanErrors = () => {
    let isValid = true;
    if (!paymentMethodCash) {
      methods.clearErrors([
        'paymentPlans.0.price',
        'paymentPlans.0.downPayment',
        'paymentPlans.0.duration',
      ]);

      const paymentPlans = methods.getValues('paymentPlans') || [];
      const paymentPlan = paymentPlans[0] || {};
      const price = Number(methods.getValues('price')) || 0;

      let downPayment = paymentPlan?.downPayment || 0;
      const finalDownPaymentValue =
        downPaymentType === 'amount'
          ? downPayment
          : (Number(downPayment) / 100) * paymentPlan?.price;
      downPayment = finalDownPaymentValue;

      const hasInstallmentPrice = Boolean(paymentPlan?.price);

      const isInstallmentPriceInvalid =
        hasInstallmentPrice && price > Number(paymentPlan?.price);

      if (paymentMethodCashAndInstallment && isInstallmentPriceInvalid) {
        methods.setError('paymentPlans.0.price', {
          type: 'validate',
          message: 'totalInstallmentPriceMustBeLargeThanPrice',
        });
        isValid = false;
      }

      if (!hasInstallmentPrice && !paymentMethodCash) {
        methods.setError('paymentPlans.0.price', {
          type: 'required',
          message: t('totalInstallmentPriceRequired'),
        });
        isValid = false;
      }
      if (
        !paymentPlan.downPayment &&
        (hasDownPayment || paymentMethodInstallment)
      ) {
        methods.setError('paymentPlans.0.downPayment', {
          type: 'required',
          message: t('downPaymentRequired'),
        });
        isValid = false;
      }
      if (!paymentPlan.duration) {
        methods.setError('paymentPlans.0.duration', {
          type: 'required',
          message: t('installmentsYearsRequired'),
        });
        isValid = false;
      }

      if (
        paymentPlan?.price &&
        paymentPlan?.downPayment &&
        downPayment >= paymentPlan?.price
      ) {
        methods.setError('paymentPlans.0.downPayment', {
          type: 'validate',
          message: 'downPaymentMustBeLessThanTotalInstallmentPrice',
        });
        isValid = false;
      }
      if (
        paymentMethodCashAndInstallment &&
        paymentPlan?.price &&
        paymentPlan?.downPayment &&
        downPayment < paymentPlan?.price * 0.01
      ) {
        methods.setError('paymentPlans.0.downPayment', {
          type: 'validate',
          message: 'downPaymentMustBeAtLeastOnePercent',
        });
        isValid = false;
      }
      if (Number(paymentPlan?.duration) > MAX_DAYS) {
        methods.setError('paymentPlans.0.duration', {
          type: 'validate',
          message: 'durationMustBeLessThan30Years',
        });
        isValid = false;
      }
    }
    return isValid;
  };

  const submitListingDetails = async (data: addListingDetailsType) => {
    if (paymentMethodInstallment) data.price = totalPropertyPrice;
    const addListingDetailsData = saveListingData(`${id}`, data);
    delete addListingDetailsData.data.installmentPeriodType;

    if (paymentMethod !== PAYMENT_METHOD_CASH_KEY && !isForRent) {
      const finalDownPaymentValue =
        downPaymentType === 'amount'
          ? addListingDetailsData.data?.paymentPlans[0]?.downPayment
          : (Number(addListingDetailsData.data?.paymentPlans[0]?.downPayment) /
              100) *
            addListingDetailsData.data?.paymentPlans?.[0]?.price;
      addListingDetailsData.data.paymentPlans[0].downPayment = Math.round(
        finalDownPaymentValue,
      );
    }

    if (paymentMethod === PAYMENT_METHOD_CASH_KEY && !isForRent)
      delete addListingDetailsData.data.paymentPlans;

    const res = await addListingDetails({
      id,
      body: addListingDetailsData.data,
    });
    if ('data' in res && id) {
      dispatch(closeModal('installmentSummaryModal'));
      navigate(ADD_LISTING_URL.EDIT(id));
    }
    if ('error' in res) {
      const error = res.error as FetchBaseQueryError;
      alert('error message: ' + (error.data as any)?.message);
    }
  };

  const onSubmit = async (data: addListingDetailsType) => {
    if (!paymentMethodCash && !isForRent) {
      const isValid = isValidPaymentPlanErrors();
      if (!isValid) return;
    }

    if (!paymentMethodInstallment && !Number(data.price)) {
      methods.setError('price', {
        type: 'validate',
        message: 'priceError',
      });
      return;
    }

    if (paymentMethodInstallment) {
      const paymentPlan = data.paymentPlans?.[0];
      setInstallmentSummaryState({
        downPaymentType,
        downPayment: paymentPlan?.downPayment,
        duration: paymentPlan?.duration,
        installmentPeriodType:
          methods.getValues('installmentPeriodType') || 'years',
        overPrice: paymentPlan?.overPrice,
        price: paymentPlan?.price || 0,
        securityDeposit: paymentPlan?.securityDeposit,
      });
      dispatch(openModal('installmentSummaryModal'));
      return;
    }

    await submitListingDetails(data);
  };

  useEffect(() => {
    methods.clearErrors();
  }, [paymentMethod]);

  useEffect(() => {
    if (methods.formState.errors?.price && !paymentMethodCash && !isForRent) {
      isValidPaymentPlanErrors();
    }
  }, [methods.formState.errors]);

  if (isFetching || isLoadingCustomField) return <Loader />;

  return (
    <div className="flex flex-col gap-4x w-full">
      <p className="text-title_2">{t('listingPrice')}</p>
      <FormProvider {...methods}>
        <div className="flex flex-col gap-5x md:gap-6x">
          <div className="flex flex-col gap-4x">
            <PaymentMethod
              customFieldsData={customFieldsData}
              listing={listingDetails}
            />
            {isForRent && <RentalDuration listing={listingDetails} />}
            {!paymentMethodInstallment && (
              <div className="flex flex-col gap-2x">
                <PriceInput listing={listingDetails} />
                <DailyPriceInput />
                <WeeklyInputPrice />
              </div>
            )}
            {!isForRent && paymentMethodInstallment && (
              <>
                {!paymentMethodInstallment && (
                  <div className="bg-gray text-gray w-full h-[1px]" />
                )}
                <div className="flex flex-col gap-3x">
                  <ContractPrice defaultContractPrice={defaultContractPrice} />
                  <OverPrice defaultOverPrice={defaultOverPrice} />
                  <SecurityDepositInput
                    defaultSecurityDeposit={defaultSecurityDeposit}
                  />
                  {totalPropertyPrice ? (
                    <p className="text-body-2 text-orange_dark_1 flex items-center">
                      <span>
                        {t('totalPropertyPrice')}:{' '}
                        {totalPropertyPrice.toLocaleString()} {t('pound')}
                        {securityDeposit ? (
                          <span className="lowercase">
                            {' '}
                            + {t('securityDeposit')}
                          </span>
                        ) : (
                          ''
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
                <div className="bg-gray text-gray w-full h-[1px]" />
              </>
            )}

            {!isForRent && paymentMethodCashAndInstallment && (
              <TotalInstallmentPrice listing={listingDetails} />
            )}
            {!isForRent && !paymentMethodCash && (
              <>
                <DownPaymentInput
                  listing={listingDetails}
                  hasDownPayment={hasDownPayment}
                  setHasDownPayment={setHasDownPayment}
                />
                <InstallmentsUnit listing={listingDetails} />
              </>
            )}
          </div>

          <div>
            <Button
              onClick={methods.handleSubmit(onSubmit)}
              variant="primary"
              size="medium"
              className="w-[135px]"
              isLoading={isLoadingAddListingDetails}
            >
              {t('follow')}
            </Button>
          </div>
        </div>
      </FormProvider>
      <InstallmentSummaryModal
        summaryState={InstallmentSummaryState}
        isLoading={isLoadingAddListingDetails}
        onClose={() => dispatch(closeModal('installmentSummaryModal'))}
        onSubmit={async () => {
          if (!InstallmentSummaryState) return;
          await submitListingDetails(methods.getValues());
        }}
      />
    </div>
  );
};

export default PriceStep;
